import { yieldToMainThread } from '@theme/utilities';
import { Component } from '@theme/component';

/**
 * FlyToCart custom element for animating product images to cart
 * This component creates a visual effect of a product "flying" to the cart when added
 */
class FlyToCart extends Component {
  /** @type {Element} */
  source;

  /** @type {boolean} */
  useSourceSize = false;

  /** @type {Element} */
  destination;

  connectedCallback() {
    super.connectedCallback();

    // Check if both elements are already laid out and visible
    const sourceRect = this.source?.getBoundingClientRect();
    const destinationRect = this.destination?.getBoundingClientRect();

    if (
      sourceRect &&
      destinationRect &&
      sourceRect.width > 0 &&
      sourceRect.height > 0 &&
      destinationRect.width > 0 &&
      destinationRect.height > 0
    ) {
      this.#animate(sourceRect, destinationRect);
      return;
    }

    // Fallback: If elements are not yet visible/laid out, observe them with a safety timeout
    /** @type {DOMRectReadOnly | null} */
    let obsSourceRect = null;
    /** @type {DOMRectReadOnly | null} */
    let obsDestinationRect = null;

    const timeoutId = setTimeout(() => {
      intersectionObserver.disconnect();

      const fallbackSourceRect = (sourceRect && sourceRect.width > 0) ? sourceRect : this.source?.getBoundingClientRect();
      const fallbackDestRect = (destinationRect && destinationRect.width > 0) ? destinationRect : this.destination?.getBoundingClientRect();

      if (
        fallbackSourceRect &&
        fallbackDestRect &&
        (fallbackSourceRect.width > 0 || fallbackSourceRect.height > 0) &&
        (fallbackDestRect.width > 0 || fallbackDestRect.height > 0)
      ) {
        this.#animate(fallbackSourceRect, fallbackDestRect);
      } else {
        document.dispatchEvent(new CustomEvent('fly-to-cart-complete'));
        this.remove();
      }
    }, 500);

    const intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === this.source) {
          obsSourceRect = entry.boundingClientRect;
        } else if (entry.target === this.destination) {
          obsDestinationRect = entry.boundingClientRect;
        }
      });

      if (
        obsSourceRect &&
        obsDestinationRect &&
        obsSourceRect.width > 0 &&
        obsDestinationRect.width > 0
      ) {
        clearTimeout(timeoutId);
        intersectionObserver.disconnect();
        this.#animate(obsSourceRect, obsDestinationRect);
      }
    });

    if (this.source) intersectionObserver.observe(this.source);
    if (this.destination) intersectionObserver.observe(this.destination);
  }

  /**
   * Animates the flying thingy along the bezier curve.
   * @param {DOMRectReadOnly} sourceRect - The bounding client rect of the source.
   * @param {DOMRectReadOnly} destinationRect - The bounding client rect of the destination.
   */
  #animate = async (sourceRect, destinationRect) => {
    //Define bezier curve points
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2,
      y: destinationRect.top + destinationRect.height / 2,
    };

    // Position the flying thingy back to the start point
    if (this.useSourceSize) {
      this.style.setProperty('--width', `${sourceRect.width}px`);
      this.style.setProperty('--height', `${sourceRect.height}px`);
    }
    this.style.setProperty('--start-x', `${startPoint.x}px`);
    this.style.setProperty('--start-y', `${startPoint.y}px`);
    this.style.setProperty('--travel-x', `${endPoint.x - startPoint.x}px`);
    this.style.setProperty('--travel-y', `${endPoint.y - startPoint.y}px`);

    await yieldToMainThread();

    await Promise.allSettled(this.getAnimations().map((a) => a.finished));
    document.dispatchEvent(new CustomEvent('fly-to-cart-complete'));
    this.remove();
  };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
